import { create } from 'zustand';
import axiosInstance from '../api/api';
import { API_ROUTES } from '../config/api-routes';
import { normalizeAppPrompts } from '../components/Prompts/promptsV2Utils';
import { normalizeSkillsList } from '../utils/userProfile';
import {
  getUserFacingError,
  isAuthFetchError,
  isRecoverableFetchError,
} from '../utils/userFacingError';
import { useUserStore } from './userStore';

type SessionStatus = 'idle' | 'loading' | 'ready' | 'error';

type BootstrapResult = {
  user: unknown;
  prompts: unknown[];
  portfolio: unknown | null;
};

type FetchOptions = { force?: boolean };

type QuietRequestConfig = { suppressErrorToast?: boolean };

function quietGet<T>(url: string) {
  return axiosInstance.get<T>(url, {
    suppressErrorToast: true,
  } as QuietRequestConfig);
}

function normalizeUserFromApi(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') {
    return raw;
  }
  const user = { ...(raw as Record<string, unknown>) };
  if ('skills' in user) {
    user.skills = normalizeSkillsList(user.skills);
  }
  return user;
}

async function fetchUserFromApi() {
  const response = await quietGet(API_ROUTES.USER.GET_CURRENT_USER);
  const data = response.data;
  const raw = (data as { user?: unknown })?.user ?? data;
  return normalizeUserFromApi(raw);
}

async function fetchPromptsFromApi() {
  const response = await quietGet(API_ROUTES.PROMPTS.GET_PROMPTS);
  if (!response || response.status !== 200) return [];
  const data = response.data;
  const raw = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return normalizeAppPrompts(raw);
}

async function fetchPortfolioFromApi() {
  try {
    const response = await quietGet(API_ROUTES.PORTFOLIO.GET_PORTFOLIO);
    if (!response || response.status !== 200) return null;
    return (response.data as { portfolio?: unknown })?.portfolio ?? null;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

type SafeFetchResult<T> = {
  value: T;
  error: unknown | null;
};

async function safeFetch<T>(fn: () => Promise<T>, fallback: T): Promise<SafeFetchResult<T>> {
  try {
    return { value: await fn(), error: null };
  } catch (err) {
    if (isAuthFetchError(err)) {
      throw err;
    }
    return { value: fallback, error: err };
  }
}

function connectionMessageFromErrors(errors: unknown[]): string | null {
  const recoverable = errors.filter(isRecoverableFetchError);
  if (recoverable.length === 0) {
    return null;
  }
  return getUserFacingError(
    recoverable[0],
    'We could not reach the server. Showing your last saved data.'
  );
}

let bootstrapInFlight: Promise<BootstrapResult> | null = null;
let userInFlight: Promise<unknown> | null = null;
let promptsInFlight: Promise<unknown[]> | null = null;
let portfolioInFlight: Promise<unknown | null> | null = null;

function resetInFlight() {
  bootstrapInFlight = null;
  userInFlight = null;
  promptsInFlight = null;
  portfolioInFlight = null;
}

interface SessionState {
  currentUser: unknown | null;
  prompts: unknown[];
  portfolio: unknown | null;
  status: SessionStatus;
  lastFetchedAt: number | null;
  connectionMessage: string | null;

  seedFromLogin: (user: unknown) => void;
  setCurrentUser: (user: unknown) => void;
  updateCurrentUser: (partial: Record<string, unknown>) => void;
  setPrompts: (prompts: unknown[]) => void;
  setPortfolio: (portfolio: unknown | null) => void;
  clearSession: () => void;

  bootstrapSession: (opts?: FetchOptions) => Promise<BootstrapResult>;
  refreshUser: (opts?: FetchOptions) => Promise<unknown>;
  refreshPrompts: (opts?: FetchOptions) => Promise<unknown[]>;
  refreshPortfolio: (opts?: FetchOptions) => Promise<unknown | null>;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  currentUser: null,
  prompts: [],
  portfolio: null,
  status: 'idle',
  lastFetchedAt: null,
  connectionMessage: null,

  seedFromLogin: (user) => {
    const normalizedUser =
      user && typeof user === 'object'
        ? {
            ...(user as Record<string, unknown>),
            skills: normalizeSkillsList((user as Record<string, unknown>).skills),
          }
        : user;
    set({
      currentUser: normalizedUser,
      status: 'idle',
      lastFetchedAt: null,
      connectionMessage: null,
    });
    if (user) useUserStore.getState().setUser(user as never);
  },

  setCurrentUser: (user) => {
    set({ currentUser: user, status: user ? 'ready' : get().status });
    if (user) useUserStore.getState().setUser(user as never);
  },

  updateCurrentUser: (partial) => {
    const prev = get().currentUser as Record<string, unknown> | null;
    if (!prev) return;
    const patch = { ...partial } as Record<string, unknown>;
    if ('skills' in patch) {
      patch.skills = normalizeSkillsList(patch.skills);
    }
    const next = { ...prev, ...patch };
    set({ currentUser: next });
    useUserStore.getState().updateUser(patch as never);
  },

  setPrompts: (prompts) =>
    set({
      prompts: normalizeAppPrompts(Array.isArray(prompts) ? prompts : []),
    }),

  setPortfolio: (portfolio) => set({ portfolio }),

  clearSession: () => {
    resetInFlight();
    set({
      currentUser: null,
      prompts: [],
      portfolio: null,
      status: 'idle',
      lastFetchedAt: null,
      connectionMessage: null,
    });
  },

  bootstrapSession: async (opts) => {
    const state = get();
    if (
      !opts?.force &&
      state.status === 'ready' &&
      state.currentUser != null &&
      state.lastFetchedAt != null
    ) {
      return {
        user: state.currentUser,
        prompts: normalizeAppPrompts(Array.isArray(state.prompts) ? state.prompts : []),
        portfolio: state.portfolio,
      };
    }

    if (!opts?.force && bootstrapInFlight) {
      return bootstrapInFlight;
    }

    bootstrapInFlight = (async () => {
      const cached = get();
      set({ status: 'loading' });
      try {
        const [userResult, promptsResult, portfolioResult] = await Promise.all([
          safeFetch(fetchUserFromApi, cached.currentUser),
          safeFetch(fetchPromptsFromApi, cached.prompts),
          safeFetch(fetchPortfolioFromApi, cached.portfolio),
        ]);

        const errors = [userResult.error, promptsResult.error, portfolioResult.error].filter(
          Boolean
        ) as unknown[];
        const connectionMessage = connectionMessageFromErrors(errors);
        const user = userResult.value ?? cached.currentUser;
        const safePrompts = normalizeAppPrompts(
          Array.isArray(promptsResult.value) ? promptsResult.value : []
        );
        const portfolio = portfolioResult.value ?? cached.portfolio ?? null;
        const syncSucceeded = errors.length === 0;

        set({
          currentUser: user,
          prompts: safePrompts,
          portfolio,
          status: user ? 'ready' : 'error',
          lastFetchedAt: syncSucceeded ? Date.now() : cached.lastFetchedAt,
          connectionMessage,
        });
        if (user) useUserStore.getState().setUser(user as never);
        return { user, prompts: safePrompts, portfolio };
      } catch (err) {
        if (isAuthFetchError(err)) {
          set({ status: 'error', connectionMessage: null });
          throw err;
        }
        const cachedAfterFailure = get();
        const connectionMessage = isRecoverableFetchError(err)
          ? getUserFacingError(err, 'We could not reach the server. Showing your last saved data.')
          : getUserFacingError(err);
        set({
          status: cachedAfterFailure.currentUser ? 'ready' : 'error',
          connectionMessage,
        });
        return {
          user: cachedAfterFailure.currentUser,
          prompts: normalizeAppPrompts(
            Array.isArray(cachedAfterFailure.prompts) ? cachedAfterFailure.prompts : []
          ),
          portfolio: cachedAfterFailure.portfolio,
        };
      } finally {
        bootstrapInFlight = null;
      }
    })();

    return bootstrapInFlight;
  },

  refreshUser: async (opts) => {
    const cached = get().currentUser;
    if (!opts?.force && cached) return cached;
    if (!opts?.force && userInFlight) return userInFlight;

    userInFlight = safeFetch(fetchUserFromApi, cached)
      .then(({ value, error }) => {
        if (error) {
          set({
            connectionMessage: connectionMessageFromErrors([error]),
            status: cached ? 'ready' : 'error',
          });
          return value ?? cached;
        }
        set({
          currentUser: value,
          status: 'ready',
          lastFetchedAt: Date.now(),
          connectionMessage: null,
        });
        if (value) useUserStore.getState().setUser(value as never);
        return value;
      })
      .finally(() => {
        userInFlight = null;
      });

    return userInFlight;
  },

  refreshPrompts: async (opts) => {
    const state = get();
    if (!opts?.force && state.status === 'ready' && state.prompts.length > 0) {
      return state.prompts;
    }
    if (!opts?.force && promptsInFlight) return promptsInFlight;

    promptsInFlight = safeFetch(fetchPromptsFromApi, state.prompts)
      .then(({ value, error }) => {
        const safe = normalizeAppPrompts(Array.isArray(value) ? value : []);
        if (error) {
          set({
            connectionMessage: connectionMessageFromErrors([error]),
          });
          return safe;
        }
        set({
          prompts: safe,
          lastFetchedAt: Date.now(),
          connectionMessage: null,
        });
        return safe;
      })
      .finally(() => {
        promptsInFlight = null;
      });

    return promptsInFlight;
  },

  refreshPortfolio: async (opts) => {
    const state = get();
    if (!opts?.force && state.status === 'ready' && state.portfolio != null) {
      return state.portfolio;
    }
    if (!opts?.force && portfolioInFlight) return portfolioInFlight;

    portfolioInFlight = safeFetch(fetchPortfolioFromApi, state.portfolio)
      .then(({ value, error }) => {
        if (error) {
          set({
            connectionMessage: connectionMessageFromErrors([error]),
          });
          return value ?? state.portfolio ?? null;
        }
        set({
          portfolio: value ?? null,
          lastFetchedAt: Date.now(),
          connectionMessage: null,
        });
        return value ?? null;
      })
      .finally(() => {
        portfolioInFlight = null;
      });

    return portfolioInFlight;
  },
}));

/** Use outside React components (avoids `useSessionStore` unused-import lint). */
export const getSessionStore = () => useSessionStore.getState();
